import { DbMetadataFieldInfos } from "../../../../Core/Api/Types/db-metadata-field-infos";
import { DbAssetObjectInfos } from "../../../../Core/Api/Types/db-asset-object-infos";
export interface RuleToken {
  type: "condition" | "value";
  testMeta?: string;
  checkValue?: string;
  testMeta2?: string | null;
  checkValue2?: string | null;
  valueIfTrue?: string;
  valueIfFalse?: string;
  value?: string;
}

export interface TokenizedRuleInfos {
  tokens: RuleToken[];
  index: number;
}

export interface TokenizedNamingRule {
  rules: TokenizedRuleInfos[];
  index: number;
}

export const computeNewToken = (type: "2-conditions" | "value" | "condition") => {
  switch (type) {
    case "2-conditions":
      return {
        type: "condition" as "condition" | "value",
        testMeta: "",
        testMeta2: "",
        checkValue: "",
        checkValue2: "",
        valueIfTrue: "",
        valueIfFalse: "",
        value: "",
      } as RuleToken;

    case "condition":
      return {
        type: "condition" as "condition" | "value",
        testMeta: "",
        checkValue: "",
        valueIfTrue: "",
        valueIfFalse: "",
      } as RuleToken;
    case "value":
      return { type: "value" as "condition" | "value", value: "" } as RuleToken;
  }
};

export const computeNewRule = (type: "2-conditions" | "value" | "condition", index: number) =>
  ({
    tokens: [computeNewToken(type)],
    index: index,
  } as TokenizedRuleInfos);

export const generateRulesFromTokens = (rules: TokenizedRuleInfos[]) => {
  let results: string[] = [];
  for (let rule of rules) {
    let { tokens } = rule;
    let ruleStr = "";
    for (let token of tokens) {
      let str = "";
      if (token.type === "value" && token.value) str = token.value;
      if (token.type === "condition") {
        str = str.concat("[[");
        if (token.testMeta) {
          str = str.concat(token.testMeta + " == ");
        }
        if (token.checkValue !== undefined) {
          str = str.concat(`'${token.checkValue}'`);
          if (token.testMeta2) {
            str = str.concat(" && ");
          } else {
            str = str.concat(" ? ");
          }
        }
        if (token.testMeta2) {
          str = str.concat(token.testMeta + " == ");
        }
        if (token.checkValue2 !== undefined) {
          str = str.concat(`'${token.checkValue2}'` + " ? ");
        }
        str = str.concat(`'${token.valueIfTrue ?? "''"}'`);
        str = str.concat(" : ");
        str = str.concat(`'${token.valueIfFalse ?? "''"}'`);
        str = str.concat("]]");
      }
      if (str.length > 0 && token.type === "condition") ruleStr = ruleStr.concat(" ");
      ruleStr = ruleStr.concat(str.trim());
    }
    results.push(ruleStr.trim());
  }

  return results;
};
export const replaceMetadatas = (asset: DbAssetObjectInfos, str: string, metadataFields: DbMetadataFieldInfos[]) => {
  let noQuoteStr = str.replaceAll("'", "");
  let result = "";
  if (!noQuoteStr.includes("%")) return noQuoteStr;
  const splitString = noQuoteStr.split("%");
  const existingMetaCount = splitString.filter((subStr) => metadataFields.find((x) => x.tagName === subStr)).length;
  let missingMetaCount = 0;
  for (let subStr of splitString) {
    const existingMeta = metadataFields.find((x) => x.tagName === subStr);
    if (subStr === "assetClass") result = result.concat((asset.classification ?? "") as string);
    else if (existingMeta) {
      const assetMetaValue =
        asset.metadata?.find((x) => x.id === existingMeta.id)?.value?.label ??
        asset.metadata?.find((x) => x.id === existingMeta.id)?.value ??
        null;

      if (assetMetaValue === undefined || assetMetaValue === null) {
        missingMetaCount += 1;
        if (missingMetaCount === existingMetaCount) return "";
      }
      result = result.concat((assetMetaValue ?? "").toString());
    } else result = result.concat(subStr);
  }
  return result;
};

const checkEquility = (metaValue: any, checkValue: any) => {
  const falsyValues = ["false", "False", "'false'", "'False", false];
  const truthyValues = ["True", "true", "'true'", "'True'", true];
  return (
    metaValue == checkValue ||
    (falsyValues.includes(metaValue) && falsyValues.includes(checkValue)) ||
    (truthyValues.includes(metaValue) && truthyValues.includes(checkValue))
  );
};

export const generateAssetNameFromNr = (
  asset: DbAssetObjectInfos,
  nr: TokenizedNamingRule,
  metadataFields: DbMetadataFieldInfos[]
) => {
  let finalStr = "";
  const { rules } = nr;
  let count = 0;
  for (let rule of rules) {
    if (finalStr.length > 0 && !finalStr.trim().endsWith("-")) finalStr = finalStr.concat(" - ");
    for (let token of rule.tokens) {
      if (token.type === "value") {
        const valueParse = replaceMetadatas(asset, token.value ?? "", metadataFields);
        if (valueParse && valueParse !== "") {
          finalStr = finalStr.concat(valueParse);
        }
      } else {
        const testMetaParse = replaceMetadatas(asset, token.testMeta ?? "", metadataFields);
        const checkValueParse = replaceMetadatas(asset, token.checkValue ?? "", metadataFields);
        const checkValue2Parse = replaceMetadatas(asset, token.checkValue2 ?? "", metadataFields);
        const testMeta2Parse = replaceMetadatas(asset, token.testMeta2 ?? "", metadataFields);
        if (
          checkEquility(testMetaParse, checkValueParse) &&
          (checkEquility(testMeta2Parse, checkValue2Parse) || (!token.checkValue2 && !token.testMeta2))
        ) {
          const valueIfTrueParse = replaceMetadatas(asset, token.valueIfTrue ?? "", metadataFields);
          if (valueIfTrueParse && valueIfTrueParse !== "") {
            finalStr = finalStr.concat(valueIfTrueParse);
          }
        } else {
          const valueIfFalseParse = replaceMetadatas(asset, token.valueIfFalse ?? "", metadataFields);
          if (valueIfFalseParse && valueIfFalseParse !== "") {
            finalStr = finalStr.concat(valueIfFalseParse);
          }
        }
      }
    }
    count += 1;
  }

  return finalStr;
};

export const generateTokensFromRule = (rule: string, index) => {
  const tokens: RuleToken[] = [];
  if (!rule) return { tokens: [], index: index };
  const splitRule = rule.split("]]");
  splitRule
    .filter((x) => x.trim().length > 0)
    .forEach((x) => {
      const isolateCondition = x.split("[[");
      isolateCondition
        .filter((t) => t.trim().length > 0)
        .forEach((t) => {
          if (t.length > 0 && t.includes("?") && t.includes(":")) {
            const isDoubleTest = t.includes("&&");
            const testMeta = isDoubleTest ? `%${t.split("&&")[0].split("%")[1]}%` : `%${t.split("%")[1]}%`;
            const testMeta2 = isDoubleTest ? `%${t.split("&&")[1].split("%")[1]}%` : undefined;
            const checkValue = isDoubleTest
              ? t.split("&&")[0].split("==")[1]
                ? t.split("&&")[0].split("==")[1].split("?")[0].trim()
                : ""
              : t.split("==")[1]
              ? t.split("==")[1].split("?")[0].trim()
              : "";
            const checkValue2 = isDoubleTest
              ? t.split("&&")[1].split("==")[1]
                ? t.split("&&")[1].split("==")[1].split("?")[0].trim()
                : ""
              : undefined;

            const valueIfTrue = t.split("?")[1].split(":")[0].trim();
            const valueIfFalse = t.split(":")[1].trim();
            tokens.push({
              type: "condition",
              testMeta,
              checkValue: checkValue.replaceAll("'", ""),
              valueIfTrue: valueIfTrue.replaceAll("'", ""),
              valueIfFalse: valueIfFalse.replaceAll("'", ""),
              testMeta2,
              checkValue2: checkValue2 ? checkValue2.replaceAll("'", "") : checkValue2,
            });
          } else {
            tokens.push({ type: "value", value: t.replaceAll("'", "") });
          }
        });
    });
  return { tokens, index };
};
